import { useCallback, useState } from 'react'

import { faHeart } from '@fortawesome/pro-regular-svg-icons'
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { Tooltip } from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { GTMEcommerceItem } from '@/helpers/types/gtmTypes'

import useToggleWatchlisting from './useToggleWatchlisting'

interface WatchlistCTAProps {
  dateExpires: string
  listingId: string
  listingTitle: string
  isWatchlisted: boolean
  gtmItem?: GTMEcommerceItem
  variant?: 'icon' | 'button'
  listingEndTime?: string
  storefrontName?: string
}

const WatchlistCTAContainer = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;

  width: 2rem;
  height: 2rem;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${Colors.Semantic.Neutral.white};
  ${Shadows.MultipleCards}
  outline: 0.0625rem solid rgba(20, 26, 30, 0.05);
  outline-offset: -0.0625rem;

  &:hover:not(:disabled) {
    ${Shadows.Hover}
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

// Exported so WatchlistBidCardRemoveButton (which renders the same visual
// but, with notes-outlive-watchlist off, intercepts the click with a
// confirmation modal) shares this exact styling — see src/components/listings/watchlist/WatchlistBidCardRemoveButton.tsx.
export const WatchlistOutlineButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1px solid ${designColors.primary.default};
  border-radius: 0.25rem;
  background: ${Colors.Semantic.Neutral.white};
  color: ${designColors.primary.default};
  cursor: pointer;
  ${Typography.Subtitle2}
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${designColors.primary.backgroundLightBlue};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const WatchlistIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Info.onhover};
  font-size: 1rem;
`

export const WatchlistOutlineIcon = styled(FontAwesomeIcon)`
  color: ${designColors.primary.default};
  font-size: 0.75rem;
`

const WatchlistCTA = ({
  dateExpires,
  listingId,
  listingTitle,
  isWatchlisted,
  gtmItem,
  variant = 'icon',
  listingEndTime,
  storefrontName,
}: WatchlistCTAProps) => {
  const intl = useIntl()
  const { trackButtonClicked } = useAnalytics()

  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null
  )

  const { isCurrentlyWatchlisted, isPending, toggleWatchlistListing } =
    useToggleWatchlisting({
      listingId,
      dateExpires,
      isWatchlisted,
      gtmItem,
      listingEndTime,
      storefrontName,
    })

  const handleToggleWatchlist = useCallback(() => {
    trackButtonClicked({
      buttonName: 'toggle_listing_watched',
      is_selected: !isCurrentlyWatchlisted,
      listing_id: listingId,
      storefront_name: storefrontName,
    })

    return toggleWatchlistListing()
  }, [
    trackButtonClicked,
    isCurrentlyWatchlisted,
    toggleWatchlistListing,
    listingId,
    storefrontName,
  ])

  const removeAriaLabel = intl.formatMessage(
    { id: 'Listing.watchlist.removeAriaLabel' },
    { listingTitle }
  )
  const addAriaLabel = intl.formatMessage(
    { id: 'Listing.watchlist.addAriaLabel' },
    { listingTitle }
  )

  if (variant === 'button') {
    return (
      <WatchlistOutlineButton
        onClick={handleToggleWatchlist}
        type="button"
        disabled={isPending}
        aria-label={isCurrentlyWatchlisted ? removeAriaLabel : addAriaLabel}
        data-testid="watchlist-cta"
      >
        <WatchlistOutlineIcon
          icon={isCurrentlyWatchlisted ? faHeartSolid : faHeart}
          data-testid="watchlist-icon"
        />
        {isCurrentlyWatchlisted
          ? intl.formatMessage({ id: 'Listing.watchlist.removeButtonLabel' })
          : intl.formatMessage({ id: 'Listing.watchlist.addButtonLabel' })}
      </WatchlistOutlineButton>
    )
  }

  return (
    <>
      <WatchlistCTAContainer
        onClick={handleToggleWatchlist}
        type="button"
        ref={setButtonElement}
        tabIndex={0}
        disabled={isPending}
        aria-label={isCurrentlyWatchlisted ? removeAriaLabel : addAriaLabel}
        data-testid="watchlist-cta"
      >
        <WatchlistIcon
          icon={isCurrentlyWatchlisted ? faHeartSolid : faHeart}
          data-testid="watchlist-icon"
        />
      </WatchlistCTAContainer>
      <Tooltip referenceElement={buttonElement}>
        {isCurrentlyWatchlisted
          ? intl.formatMessage({ id: 'Listing.watchlist.removeTooltip' })
          : intl.formatMessage({ id: 'Listing.watchlist.addTooltip' })}
      </Tooltip>
    </>
  )
}

export default WatchlistCTA
